import { SpinnerOverlayService } from 'src/app/services/spinnerOverlay.service';
import { SelectableSettings, DataStateChangeEvent, GridComponent, PageChangeEvent, DetailExpandEvent, RowClassArgs, ExcelExportEvent } from '@progress/kendo-angular-grid';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { State, process, filterBy, SortDescriptor, FilterDescriptor, DataResult, CompositeFilterDescriptor } from '@progress/kendo-data-query';

import { Pay1SyncService } from './../../../../services/pay1Sync.service';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Component, ViewEncapsulation, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import SystemUser from 'src/app/models/user';
import { orderBy, forEach, filter, isNull, startCase, cloneDeep, map, sortBy, some, remove, mapValues, keyBy, reverse, includes, uniq } from 'lodash';
import { AccessLevel, CoreService, UserTypeCode } from 'src/app/services/core.service';
import Pay1SyncHoldErrorOverview from 'src/app/models/pay1SyncHoldErrorOverview';
import * as dayjs from 'dayjs';
import { distinct, lastValueFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Pay1SyncHold from 'src/app/models/pay1SyncHold';
import Pay1SyncLock from 'src/app/models/pay1SyncLock';
import Member from 'src/app/models/member';
import Pay1SyncStaffAction from 'src/app/models/pay1SyncStaffAction';
import Pay1SyncAudit from 'src/app/models/pay1SyncAudit';

class LevelInfo<T> {
  data: T[];
  clonedGridData: DataResult;
  state: State = {
    skip: 0
  };
  skip = 0;
  multiSelect = false;
  selectedItem: T | null;
  selectBy: string;
  selectedKeys: string[] = [];
  columns: { [k: string]: string | {} }[];
  sort: SortDescriptor[] = [];
  filters = { showPendingRecords: true };
  filter: CompositeFilterDescriptor;

  constructor(cols?: { [k: string]: string | {} }[], selectBy?: string, multiSelect = false) {
    this.columns = cols;
    this.selectBy = selectBy;
    this.multiSelect = multiSelect;
  }

  all(predicate: (item: T) => boolean): boolean {
    if (this.selectedKeys.length > 0)
      return this.selected.filter(predicate).length === this.selectedKeys.length;

    return false;
  }

  get selected(): T[] {
    if (this.selectedKeys && this.selectedKeys.length)
      return this.clonedGridData.data.filter(d => this.selectedKeys.indexOf(d[this.selectBy]) > -1);

    return [];
  }

  get firstSelected(): T | null {
    const s = this.selected;

    if (s && s.length)
      return s[0];

    return null;
  }
}

@UntilDestroy()
@Component({
  selector: 'sync-err-handling',
  templateUrl: 'sync.error.handling.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class HCASyncErrorHandlingComponent implements OnInit {
  icons = {
    faPlus,
    faMinus,
  };
  systemUser: SystemUser;
  filters: {showPay1Errors: boolean, showB247Holds: boolean, showAwaiting: boolean, showUnhandledErrors: boolean, showBlockedByHCA: boolean, showProcessed: boolean, filterString: string | null} = {
      showPay1Errors: true,
      showB247Holds: true,
      showAwaiting: false,
      showUnhandledErrors: true,
      showBlockedByHCA: true,
      showProcessed: false,
      filterString: null,
    };
  data: {
    l1: LevelInfo<Pay1SyncHoldErrorOverview>,
    l2: LevelInfo<Pay1SyncHold>,
    l3: LevelInfo<any>,
    locks: LevelInfo<Pay1SyncLock>,
    searchLocks: LevelInfo<Member>,
  } = {
    l1: new LevelInfo<Pay1SyncHoldErrorOverview>([
      { field: 'lastName', title: 'Sub last name', format: 'string' },
      { field: 'firstName', title: 'Sub first name', format: 'string' },
      { field: 'createdDate', title: 'Created date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'errorDate', title: 'Error date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'program', title: 'Program', format: 'string' },
      { field: 'errorCount', title: 'Error count', format: 'string' },
      { field: 'agency', title: 'Agency', format: 'string' }
    ]),
    l2: new LevelInfo<Pay1SyncHold>([
      { field: 'tranId', title: 'Tran ID', format: 'number' },
      { field: 'blockedByTranIds', title: 'Blocked by Transaction IDs', format: 'string' },
      { field: 'isSubscriber', title: 'Is sub?', format: 'boolean' },
      { field: 'ssn', title: 'SSN', format: 'string' },
      { field: 'lastName', title: 'Last name', format: 'string' },
      { field: 'firstName', title: 'Sub First name', format: 'string' },
      { field: 'transactionTypeCode', title: 'Transaction type', format: 'string' },
      { field: 'asOfDate', title: 'As of date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'sentToPay1Date', title: 'Sent to PAY1', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'processedByPay1Date', title: 'Processed by PAY1', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'pay1ErrorDate', title: 'Error or hold date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'pay1ErrorInfo', title: 'PAY1 error', format: 'string' },
      { field: 'b247ErrorInfo', title: 'B24/7 hold', format: 'string' },
      { field: 'doNotSendToPay1', title: 'Never Send', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'createdDate', title: 'Created date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'staffActionCount', title: 'Staff actions', format: 'number' },
    ], 'pay1SyncHoldId', true),
    l3: new LevelInfo<any>(),
    locks: new LevelInfo<Pay1SyncLock>([
      { field: 'subscriberMemberId', title: 'Sub GUID', format: 'string' },
      { field: 'subscriberName', title: 'Sub name', format: 'string' },
      { field: 'subscriberSsn', title: 'Sub SSN', format: 'string' },
      { field: 'memberName', title: 'Member name', format: 'string' },
      { field: 'memberSsn', title: 'Member SSN', format: 'string' },
      { field: 'effectiveStartDate', title: 'Start date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'effectiveEndDate', title: 'End date', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
    ], 'pay1SyncLockId'),
    searchLocks: new LevelInfo<Member>([
      { field: 'memberId', title: 'Member ID', format: 'string' },
      { field: 'firstName', title: 'First name', format: 'string' },
      { field: 'lastName', title: 'Last name', format: 'string' },
      { field: 'socialSecurityNumber', title: 'SSN', format: 'string' },
      { field: 'birthDate', title: 'DOB', format: { date: 'mm/dd/yyyy' }, filter: 'date' },
      { field: 'isSubscriberInd', title: 'Is sub?', format: 'boolean' },
    ], 'memberId')
  };
  l3: string;
  savedRecords: string[] = [];
  now = dayjs().format('MMDDYYYY');
  editableFields = [
      'home_agy', 'last_name', 'first_name', 'middle_name', 'suffix', 'birth_dt', 'sex', 'gender', 
      'res_addr_1', 'res_addr_2', 'res_city', 'res_state', 'res_zip', 'residential_county', 'residential_country', 'res_addr_eff_dt', 
      'mailing_addr_1', 'mailing_addr_2', 'mailing_city', 'mailing_state', 'mailing_zip', 'mailing_country', 'mail_stop', 
      'email_address', 'opt_in_out_ind', 'home_phone', 'work_phone', 
      'elig_type', 'elig_rsn_cd', 'elig_eff_dt', 'elig_apply_dt', 'regain_elig_dt', 
      'salary', 'pay_method', 'marital_sts', 'marriage_dt', 'divorce_dt', 'deceased_dt', 
      'retire_dt', 'cobra_qualify_cd', 'cobra_term_dt', 'originating_agency', 'originating_ssn', 'elect_period_end_dt', 
      'hlth_enr_cd', 'hlth_enr_rsn_cd', 'hlth_tsa', 'hlth_tsa_eff_dt', 'hlth_eff_dt', 'hlth_end_dt', 'hlth_carr', 
      'medicare_a_ind', 'medicare_a_dt', 'medicare_b_ind', 'medicare_b_dt', 'signature_date_dt', 'mcare_id_mbi_hicn', 'wip_ind', 'wip_eff_dt', 
      'dntl_enr_cd', 'dntl_enr_rsn_cd', 'dntl_eff_dt', 'dntl_end_dt', 'dntl_carr', 
      'visn_enr_cd', 'visn_enr_rsn_cd', 'visn_eff_dt', 'visn_end_dt', 'visn_carr', 
      'basic_life_enr', 'basic_life_eff_dt', 'ltd_basic_enr', 'ltd_basic_eff_dt', 
      'ltd_opt_enr', 'ltd_opt_eff_dt', 'ltd_opt_elim_period', 'ltd_opt_percent_sebb', 
      'aca_status', 'union_member_ind', 'union_member_dt', 'medicare_d_ind', 'medicare_d_dt', 'signature_dis_date_dt',
      'family_comp', 'family_comp_eff_date',

      'subscriber_home_agy', 'dependent_type', 
      'dep_last_name', 'dep_first_name', 'dep_middle_name', 'dep_suffix', 'dep_birth_dt', 'dep_sex', 'dep_gender', 
      'dep_residential_addr_1', 'dep_residential_addr_2', 'dep_residential_city', 'dep_residential_state', 'dep_residential_zip', 'dep_residential_country', 
      'dep_spousal_surcharge_ssa_ind', 'dep_spousal_surcharge_ssa_eff_dt', 'dep_60day_term_ind', 
      'dep_relationship', 'dep_qualify_reason', 'dep_verify_source', 'dep_verify_status', 'dep_verify_status_dt', 
      'dep_certification_ind', 'dep_certification_eff_dt', 'dep_certification_end_dt', 
      'dep_health_enrollment_cd', 'dep_health_enrollment_reason_cd', 'dep_health_tobacco_tsa_ind', 'dep_health_tobacco_tsa_eff_dt', 'dep_health_eff_dt', 'dep_health_end_dt', 
      'dep_medicare_a_ind', 'dep_medicare_a_dt', 'dep_medicare_b_ind', 'dep_medicare_b_dt', 'dep_signature_dt', 'dep_medicare_id_mbi_hicn', 
      'dep_dental_enrollment_cd', 'dep_dental_enrollment_reason_cd', 'dep_dental_eff_dt', 'dep_dental_end_dt', 
      'dep_vision_enrollment_cd', 'dep_vision_enrollment_reason_cd', 'dep_vision_eff_dt', 'dep_vision_end_dt', 
      'spouse_death_divorce_dt', 'dep_medicare_d_ind', 'dep_medicare_d_dt', 'dep_signature_dis_date_dt'
    ];
  isHcaSysAdmin = false;
  showActiveLocksOnly = true;
  showSyncLocks = false;
  showSearchLocks = false;
  addLockMemberIdOrSsn: string | null = null;
  addLock = false;
  locksSelectable: SelectableSettings =  { mode: 'multiple'};
  lockAccountLevel = true;
  pendingCount: number | null = null;
  processedCount: number | null = null;
  ignoredCount: number | null = null;
  visibleStaffActions: Pay1SyncStaffAction[] | null | undefined;
  visibleAudits: Pay1SyncAudit[] | null | undefined;
  @ViewChild('kendoGrid') public kendoGrid: GridComponent;

  constructor(
    private route: ActivatedRoute,
    private spinnerOverlayService: SpinnerOverlayService,
    private coreService: CoreService,
    private pay1SyncService: Pay1SyncService
  ) {}

  async onSearchSubmitted(filterString: string): Promise<void> {
    this.filters.filterString = filterString;

    await this.updateFilters();
  }

  async updateFilters(): Promise<void> {
    await this.refresh();
  }

  updateAccountFilters(): void {
    this.loadItems(this.data.l2);
  }

  async ngOnInit(): Promise<void> {
    await this.refresh();

    this.route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.systemUser = data.user;

      this.isHcaSysAdmin = this.coreService.systemUserHasAccess(AccessLevel.SystemAdmin, UserTypeCode.HCA);
    });
  }

  async refresh(): Promise<void> {
    this.spinnerOverlayService.show();

    const transactions = await lastValueFrom(this.pay1SyncService.fetchErroredTransactions(this.filters));

    this.collapseL1GridSelection();

    this.showActiveLocksOnly = false;
    this.showSyncLocks = false;
    this.showSearchLocks = false;
    this.data.l1.selectedItem = null;
    this.data.l2.selectedItem = null;
    this.data.l3.selectedItem = null;

    this.data.l1.data = sortBy(transactions, t => t.errorDate);
    this.reSetDataForGrid(this.data.l1);

    this.spinnerOverlayService.hide();
  }

  outputField(dataItem, col): any {
    const val = dataItem[col.field];

    if (typeof col.format === 'function') {
      return col.format(val);
    }

    if (val && col.format?.date) {
      return dayjs(val).format(col.format.date.toUpperCase());
    }

    return val;
  }

  public dataStateChange<T>(state: DataStateChangeEvent, info: LevelInfo<T>): void {
    info.state = state;
    info.sort = state.sort;
    info.skip = state.skip;
    info.filter = state.filter;
    
    this.loadItems(info);
  }

  private loadItems<T>(info: LevelInfo<T>): void {
    var data = cloneDeep(info.data);

    if (data && data.length && data[0] instanceof Pay1SyncHold) {
      // Apply filters
      if (!this.data.l2.filters['showPendingRecords'])
        data = data.filter((d: Pay1SyncHold) => !d.pending);

      if (!this.data.l2.filters['showProcessedRecords'])
        data = data.filter((d: Pay1SyncHold) => !d.processed);

      if (!this.data.l2.filters['showDoNotSendRecords'])
        data = data.filter((d: Pay1SyncHold) => !d.doNotSend);
    }

    const state = { sort: info.sort, take: 20, skip: info.skip, filter: info.filter };

    info.clonedGridData = process(data, state);//{
  }

  l2RowClass(context: RowClassArgs) {
    return { 
      processed: context.dataItem.processed,
      doNotSend: context.dataItem.doNotSend,
      pending: context.dataItem.pending,
    };
  }

  finishManaging(): void {
    if (this.data.l1.selectedItem) {
      // Check for unsent changes in L2 table
      const unsentItems = this.data.l2.data.filter(l2 => this.savedRecords.indexOf(l2.pay1SyncHoldId) > -1);

      if (unsentItems.length) {
        this.coreService.popMessage('There are unsent changes to existing records.', 'warning', 2000);
      }
    }

    this.collapseL1GridSelection();
    this.data.l1.selectedItem = null;
    this.data.l2.selectedItem = null;
    this.data.l3.selectedItem = null;
  }

  collapseL1GridSelection(): void {
    this.data.l1.data?.forEach((gd, idx) => this.kendoGrid.collapseRow(idx));
    this.data.l2.filters = { showPendingRecords: true };
  }

  async getRowDetails(event: DetailExpandEvent, finishManaging = true): Promise<void> {
    if (finishManaging) {
      this.finishManaging();
      this.collapseL1GridSelection();
    }

    this.data.l3.selectedItem = null;
    this.data.l2.selectedItem = null;

    this.data.l1.selectedItem = event.dataItem;
    event.preventDefault();

    this.spinnerOverlayService.show();

    this.data.l2.data = sortBy(await lastValueFrom(this.pay1SyncService.fetchAccountTransactions(event.dataItem.subscriberMemberId)), t => t.tranId);

    // Set up counts for the entire account (filter checkboxes)
    this.pendingCount = this.data.l2.data.filter(d => d.pending).length;
    this.processedCount = this.data.l2.data.filter(d => d.processed).length;
    this.ignoredCount = this.data.l2.data.filter(d => d.doNotSend).length;

    // Grab blocked by tran info
    this.data.l2.data.forEach((d: Pay1SyncHold) => {
      if (d.pending) {
        // Pass 1 - members that are blocked by an earlier sequence on the account (min tran_id in that sequence) -- OTHER member only
        let blockers = this.data.l2.data.filter((o: Pay1SyncHold) => o.simplifiedSequenceId < d.simplifiedSequenceId && o.pending && o.memberId !== d.memberId);

        // Pass 2 - members that have an earlier pending tran_id for themselves (self block!)
        blockers = blockers.concat(this.data.l2.data.filter((o: Pay1SyncHold) => o.memberId === d.memberId && o.pending && o.tranId < d.tranId));

        if (blockers.length)
          d.blockedByTranIds = sortBy(uniq(blockers.map((b: Pay1SyncHold) => b.tranId)));
      }
    });

    this.reSetDataForGrid(this.data.l2);
    this.spinnerOverlayService.hide();

    this.kendoGrid.expandRow(event.index);
  }

  async resendRecords(pay1SyncHoldIds: string[]): Promise<void> {
    this.spinnerOverlayService.show();

    await lastValueFrom(this.pay1SyncService.resendRecords(this.data.l1.selectedItem.subscriberMemberId, pay1SyncHoldIds));

    this.coreService.popMessage('Records will be sent again at next sync file generation', 'info', 2000);

    await this.refreshAccount(false);

    this.spinnerOverlayService.hide();
  }

  async markAsProcessed(pay1SyncHoldIds: string[]): Promise<void> {
        
    this.spinnerOverlayService.show();

    if (confirm('Are you sure you want to mark these record(s) as processed?')) {
      this.data.l3.selectedItem = null;
      this.data.l2.selectedItem = null;

      var rows = this.data.l2.data.filter(d => pay1SyncHoldIds.indexOf(d.pay1SyncHoldId) > -1);
      await Promise.all(rows.map(async (row) => await lastValueFrom(this.pay1SyncService.markAsProcessed(row.memberId, row.pay1SyncHoldId))));
      await this.refreshAccount(false);
    }

    this.spinnerOverlayService.hide();
  }

  async refreshAccount(finishManaging = true): Promise<void> {
    const e = new DetailExpandEvent(null);
    e.dataItem = this.data.l1.selectedItem;
    e.index = this.data.l1.clonedGridData.data.indexOf(e.dataItem);
    await this.getRowDetails(e, finishManaging);
  }

  async allowSync(pay1SyncHoldIds: string[]): Promise<void> {
    this.spinnerOverlayService.show();

    if (confirm('Are you sure you want to allow this record to send to PAY1?')) {
      this.data.l3.selectedItem = null;
      this.data.l2.selectedItem = null;

      var rows = this.data.l2.data.filter(d => pay1SyncHoldIds.indexOf(d.pay1SyncHoldId) > -1);
      await Promise.all(rows.map(async (row) => await lastValueFrom(this.pay1SyncService.allowSync(row.memberId, row.pay1SyncHoldId))));
      await this.refreshAccount(false);
    }

    this.spinnerOverlayService.hide();
  }

  async viewRecordL2(pay1SyncHoldIds: string[]): Promise<void> {
    let rows = this.data.l2.data.filter((d: Pay1SyncHold) => pay1SyncHoldIds.indexOf(d.pay1SyncHoldId) > -1);

    if (rows.length) {
      let row = rows[0];
    
      this.data.l3.selectedItem = null;
      // Modal is triggered off this being set, so clear it out while we set things up and set it at the end.
      this.data.l2.selectedItem = null;

      this.spinnerOverlayService.show();
      this.data.l3.data = sortBy(await lastValueFrom(this.pay1SyncService.fetchDetail(row.subscriberMemberId, row.pay1SyncHoldId)), t => t.errorDate);

      this.data.l3.columns = [];
      const data = this.data.l3.data[0];
      if (data) {
        for (const field in data) {
          if (field.includes('Date')) {
            map(data, (val, rd) => {
              data[rd][field] = val[field] && val[field] !== '0001-01-01T00:00:00-08:00' ? new Date(val[field]) : null;
            });
          }

          this.data.l3.columns.push({
            field: field,
            title: field,
            format: (field.startsWith('date') || field.includes('Date')) ? (field.includes('Time') ? '{0: MM/dd/yyyy HH:mm:ss}' : { date: 'mm/dd/yyyy' }) : 'string',
            filter: (field.startsWith('date') || field.includes('Date')) ? 'date' : null,
            width: 200
          });
        }
      }

      if (this.data.l3.columns.length > 75) {
        this.l3 = 'sub';
      } else {
        this.l3 = 'dep';
      }

      // Replicate the record; this is the "editable" version
      this.data.l3.data.push(cloneDeep(data));

      this.data.l2.selectedItem = row;

      this.reSetDataForGrid(this.data.l3);
      this.spinnerOverlayService.hide();
    }
  }

  reSetDataForGrid<T>(info: LevelInfo<T>, index?): void {
    info.skip = 0;
    this.loadItems(info);

    // Clear any selected keys in filtered areas
    if (this.data.l2.clonedGridData)
      this.data.l2.selectedKeys = this.data.l2.clonedGridData.data
        .filter(d => this.data.l2.selectedKeys.indexOf(d[this.data.l2.selectBy]) > -1)
        .map(d => d[this.data.l2.selectBy]);
    else
      this.data.l2.selectedKeys = [];
  }

  public changeSelection<T>(e, info: LevelInfo<T>): void {
    if (info.multiSelect) {
      info.selectedKeys = info.selectedKeys.filter(d => e.deselectedRows.map(r => r.dataItem[info.selectBy]).indexOf(d) === -1)
        .concat(e.selectedRows.map(r => r.dataItem[info.selectBy]));
    } else
      info.selectedKeys = e.selectedRows.map(r => r.dataItem[info.selectBy]);
  }

  public showStaffActions(actions: Pay1SyncStaffAction[]): void {
    this.visibleStaffActions = actions;
  }

  public closeStaffActions(): void {
    this.visibleStaffActions = null;
  }

  public viewAudits(audits: Pay1SyncAudit[]): void {
    this.visibleAudits = audits;
  }

  public closeAudits(): void {
    this.visibleAudits = null;
  }

  closeWizardDialogue(): void {
    this.data.l2.selectedItem = null;
  }

  async saveOutput(subscriberMemberId: string, pay1SyncHoldId: string, record: any): Promise<void> {
    this.spinnerOverlayService.show();

    await lastValueFrom(this.pay1SyncService.updateOutput(subscriberMemberId, pay1SyncHoldId, record));
    this.savedRecords.push(pay1SyncHoldId);

    this.closeWizardDialogue();

    this.spinnerOverlayService.hide();
    this.coreService.popMessage('Updated record', 'success', 2000);
  }

  async openSyncLocks(): Promise<void> {
    this.spinnerOverlayService.show();

    this.data.locks.data = sortBy(await lastValueFrom(this.pay1SyncService.getLocks(this.showActiveLocksOnly)), t => t.effectiveStartDate);
    this.reSetDataForGrid(this.data.locks);

    this.showSyncLocks = true;

    this.spinnerOverlayService.hide();
  }

  async searchLock(addLockMemberIdOrSsn: string): Promise<void> {
    this.spinnerOverlayService.show();
    
    this.data.searchLocks.data = sortBy(await lastValueFrom(this.pay1SyncService.searchLock(addLockMemberIdOrSsn)), t => t.fullName);
    this.reSetDataForGrid(this.data.searchLocks);

    this.showSearchLocks = true;

    this.spinnerOverlayService.hide();
  }

  async lockRecords(memberIds: string[], lockAccountLevel: boolean): Promise<void> {
    this.spinnerOverlayService.show();
    
    const members = this.data.searchLocks.data.filter(m => memberIds.indexOf(m.memberId) > -1);

    for(let i = 0, m; m = members[i]; i++)
      await lastValueFrom(this.pay1SyncService.addLock(m, lockAccountLevel));

    this.showSearchLocks = false;
    this.addLock = false;
    await this.openSyncLocks();

    this.spinnerOverlayService.hide();
  }

  async endLocks(pay1SyncLockIds: string[]): Promise<void> {
    this.spinnerOverlayService.show();
    
    for(let i = 0, id; id = pay1SyncLockIds[i]; i++)
      await lastValueFrom(this.pay1SyncService.unlock(id));

    await this.openSyncLocks();

    this.spinnerOverlayService.hide();
  }

  public async l1Export(): Promise<ExcelExportData> {
    this.spinnerOverlayService.show();

    const data = await lastValueFrom(this.pay1SyncService.fetchFullPageDownload(this.filters));
 
    const result: ExcelExportData = {
      data: data,
    };

    return result;
  }

  public onL1L2ExcelExport(e: ExcelExportEvent): void {
    const dataRows = e.workbook.sheets[0].rows.filter(row => row.type === 'data');
    const cellIndexesToFlatten = [11, 12];

    dataRows.forEach(row => {
      cellIndexesToFlatten.forEach(tagsCellIndex =>
        {
            const cellValue = row.cells[tagsCellIndex].value;
            
            // If the value is an array, join it into a string
            if (Array.isArray(cellValue)) {
                row.cells[tagsCellIndex].value = cellValue.join(', '); 
            }
        });
    });

    this.spinnerOverlayService.hide();
  }

  public l2Export(): ExcelExportData {
    const result: ExcelExportData = {
      data: this.data.l2.data,
    };
    return result;
  }

  public resendRecordsPredicate(item: Pay1SyncHold): boolean {
    return item.processed || item.doNotSend;
  }

  public markAsProcessedPredicate(item: Pay1SyncHold): boolean {
    return item.pending;
  }

  public allowSyncToPay1Predicate(item: Pay1SyncHold): boolean {
    return item.isHold;
  }

  readonly fieldMaxLengths: { [key: string]: number } = {
    'home_agy': 6,
    'last_name': 20,
    'first_name': 15,
    'middle_name': 3,
    'suffix': 3,
    'birth_dt': 8,
    'sex': 1,
    'gender': 1,
    'res_addr_1': 40,
    'res_addr_2': 40,
    'res_city': 30,
    'res_state': 2,
    'res_zip': 10,
    'residential_county': 2,
    'residential_country': 2,
    'res_addr_eff_dt': 8,
    'mailing_addr_1': 40,
    'mailing_addr_2': 40,
    'mailing_city': 30,
    'mailing_state': 2,
    'mailing_zip': 10,
    'mailing_country': 2,
    'mail_stop': 6,
    'email_address': 50,
    'opt_in_out_ind': 1,
    'home_phone': 10,
    'work_phone': 10,
    'elig_type': 1,
    'elig_rsn_cd': 2,
    'elig_eff_dt': 8,
    'elig_apply_dt': 8,
    'regain_elig_dt': 8,
    'salary': 8,
    'pay_method': 1,
    'marital_sts': 1,
    'marriage_dt': 8,
    'divorce_dt': 8,
    'deceased_dt': 8,
    'retire_dt': 8,
    'cobra_qualify_cd': 1,
    'cobra_term_dt': 8,
    'originating_agency': 6,
    'originating_ssn': 9,
    'elect_period_end_dt': 8,
    'hlth_enr_cd': 1,
    'hlth_enr_rsn_cd': 2,
    'hlth_tsa': 1,
    'hlth_tsa_eff_dt': 8,
    'hlth_eff_dt': 8,
    'hlth_end_dt': 8,
    'hlth_carr': 4,
    'medicare_a_ind': 1,
    'medicare_a_dt': 8,
    'medicare_b_ind': 1,
    'medicare_b_dt': 8,
    'signature_date_dt': 8,
    'mcare_id_mbi_hicn': 12,
    'wip_ind': 1,
    'wip_eff_dt': 8,
    'dntl_enr_cd': 1,
    'dntl_enr_rsn_cd': 2,
    'dntl_eff_dt': 8,
    'dntl_end_dt': 8,
    'dntl_carr': 4,
    'visn_enr_cd': 1,
    'visn_enr_rsn_cd': 2,
    'visn_eff_dt': 8,
    'visn_end_dt': 8,
    'visn_carr': 4,
    'basic_life_enr': 1,
    'basic_life_eff_dt': 8,
    'ltd_basic_enr': 1,
    'ltd_basic_eff_dt': 8,
    'ltd_opt_enr': 1,
    'ltd_opt_eff_dt': 8,
    'ltd_opt_elim_period': 3,
    'ltd_opt_percent_sebb': 5,
    'aca_status': 2,
    'union_member_ind': 1,
    'union_member_dt': 8,
    'medicare_d_ind': 1,
    'medicare_d_dt': 8,
    'signature_dis_date_dt': 8,
    'family_comp': 3,
    'family_comp_eff_date': 8
    };
}
